import { useQuery } from "@tanstack/react-query";
import { useMemo, type ReactNode } from "react";
import { useIntl } from "react-intl";

import { useTravellersSelectionContext } from "@/features/travellers/contexts/TravellersSelectionContext";
import { travellersQueryOptions } from "@/features/travellers/hooks/useTravellers";
import {
  allTravellerAgeGroups,
  type Traveller,
  type TravellerAgeGroup,
} from "@/features/travellers/types/types";
import { numericDateFormatOptions } from "@/utils/dates";

import { TravellerSelectionButton } from "./TravellersSelectionButton";

type Props = {
  onSlotClick: (ageGroup: TravellerAgeGroup, index: number) => void;
  renderAdditionalServiceControls?: (traveller: Traveller) => ReactNode;
};

export function TravellersSelection({
  onSlotClick,
  renderAdditionalServiceControls,
}: Props) {
  const intl = useIntl();
  const { data: travellers } = useQuery(travellersQueryOptions());

  const travellersById = useMemo(() => {
    const map: Record<string, Traveller> = {};
    for (const traveller of travellers || []) {
      map[traveller.id] = traveller;
    }
    return map;
  }, [travellers]);

  const { travellerSlots } = useTravellersSelectionContext();

  return (
    // additional margin to compensate for TravellerSelectionStatusDrawer which is always used together with this list
    <ul className="mb-8 space-y-2">
      {allTravellerAgeGroups.map((ageGroup) =>
        travellerSlots[ageGroup].map((travellerId, index) => (
          <li key={`${ageGroup}-${index}`}>
            <TravellerSelectionButton
              traveller={travellerId ? travellersById[travellerId] : null}
              onClick={() => {
                onSlotClick(ageGroup, index);
              }}
              renderAdditionalServicesControls={
                travellerId
                  ? () =>
                      renderAdditionalServiceControls?.(
                        travellersById[travellerId],
                      )
                  : undefined
              }
            >
              <div className="flex gap-1.5">
                <span>
                  {intl.formatMessage(
                    {
                      defaultMessage:
                        "{type, select, ADT {Adult} CHD {Child} INF {Infant} other {Traveller}} {number}",
                      description:
                        "Numbered traveller slot label (e.g. 'Adult 1', 'Child 2')",
                    },
                    {
                      type: ageGroup,
                      number: index + 1,
                    },
                  )}
                </span>
                {travellerId ? (
                  <>
                    <span>&bull;</span>
                    <span>
                      {intl.formatDate(
                        travellersById[travellerId].birthDate,
                        numericDateFormatOptions,
                      )}
                    </span>
                  </>
                ) : null}
              </div>
            </TravellerSelectionButton>
          </li>
        )),
      )}
    </ul>
  );
}
